import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import { TableVisibilitySettings } from '../types/gotypes'

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function visibilityParams(visibility: TableVisibilitySettings): Record<string, string> {
  return {
    frequency: String(visibility.frequency),
    column_percent: String(visibility.column_percent),
    row_percent: String(visibility.row_percent),
    significance: String(visibility.significance),
  }
}

/** URL of the saved-table workbook route (GET /tables/:c/:j/xlsx). */
export function buildSavedTableXlsxUrl(opts: {
  customer: string
  job: string
  tablePath: string
  visibility: TableVisibilitySettings
  filter?: string
  token?: string
  /** The dashboard the table is shown in, for the dashboard grant (DashboardGrantContext). */
  dashboard?: string | null
}): string {
  const params = new URLSearchParams({ path: opts.tablePath, ...visibilityParams(opts.visibility) })
  if (opts.dashboard) params.set('dashboard', opts.dashboard)
  // The filter makes the workbook match the filtered view on screen.
  if (opts.filter) params.set('filter', opts.filter)
  // Token as a query param is the middleware fallback for a new-tab request.
  if (opts.token) params.set('token', opts.token)
  return `${API_BASE_URL}/tables/${opts.customer}/${opts.job}/xlsx?${params.toString()}`
}

/**
 * URL for running a spec as a workbook (POST /tables/:c/:j/run?format=excel).
 * tablePath or sessionId names the source in the workbook's stamp (#1650):
 * the table it was opened from, or the agent session it came from.
 */
export function buildSpecXlsxUrl(opts: {
  customer: string
  job: string
  visibility: TableVisibilitySettings
  tablePath?: string
  sessionId?: string
}): string {
  const params = new URLSearchParams({ format: 'excel', ...visibilityParams(opts.visibility) })
  if (opts.sessionId) params.set('session', opts.sessionId)
  else if (opts.tablePath) params.set('path', opts.tablePath)
  return `${API_BASE_URL}/tables/${encodeURIComponent(opts.customer)}/${encodeURIComponent(opts.job)}/run?${params.toString()}`
}

/** Opens the saved table's workbook in a new tab, which downloads it. */
export function openSavedTableXlsx(opts: Parameters<typeof buildSavedTableXlsxUrl>[0]): void {
  const a = document.createElement('a')
  a.href = buildSavedTableXlsxUrl(opts)
  a.rel = 'noopener'
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** Runs a spec on the server and saves the workbook it returns. */
export async function downloadSpecXlsx(opts: {
  customer: string
  job: string
  spec: unknown
  visibility: TableVisibilitySettings
  fileName: string
  tablePath?: string
  sessionId?: string
}): Promise<void> {
  const response = await axios.post(buildSpecXlsxUrl(opts), opts.spec, { responseType: 'blob' })
  const blob = new Blob([response.data], { type: XLSX_MIME })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = opts.fileName.toLowerCase().endsWith('.xlsx') ? opts.fileName : `${opts.fileName}.xlsx`
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
